// src/features/projects/projectFilters.js
//
// PROJ-PR-1/PR-2 — Pure filter, stat-derivation, and selector helpers for
// the Projects module. No React, no side effects. All functions are exported
// for independent unit testing.

import { CAPEX_PROJECT_TYPES } from "../../data/projectsClient.js";
import { PROJECT_PHASE } from "../../app/tokens.js";
import { getCurrencySymbol } from "../../utils/format.js";
import { convertAmount } from "../../utils/fxConvert.js";
import { GANTT_LEGEND, mapGanttPhase } from "./hub/ganttModel.js";

export const PROJECT_SCOPE = {
  CAPITAL_MOVES: "capital_moves",
  ALL: "all",
};

const ALWAYS_CAPITAL_OR_MOVE_TYPES = new Set(["new_build", "exit", "consolidation"]);
const CATEGORY_BUDGET_TYPES = new Set(["soft", "hard", "ffe", "contingency", "other"]);

export function getProjectPriorityLabel(project) {
  const priority = project?.priority;
  return priority == null || String(priority).trim() === "" ? "Not set" : String(priority).trim();
}

function formatProjectDate(value) {
  if (!value) return "TBD";
  const raw = String(value);
  const date = new Date(raw.length === 10 ? `${raw}T00:00:00Z` : raw);
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function getProjectTargetLabel(project) {
  const date = formatProjectDate(project?.fdobDate);
  return project?.projectType === "exit" ? `Exit date: ${date}` : date;
}

function getCategoryBudget(project) {
  const explicitTotal = [
    project?.categoryBudgetGrandTotal,
    project?.projectBudgetGrandTotal,
    project?.budgetGrandTotal,
  ].find((value) => value != null && Number.isFinite(Number(value)));
  if (explicitTotal != null) {
    return {
      amount: Number(explicitTotal),
      currency: project?.categoryBudgetCurrency || project?.budgetCurrency || project?.currency || "USD",
      source: "category",
    };
  }

  const rows = [project?.projectBudgets, project?.categoryBudgets, project?.budgets]
    .find((value) => Array.isArray(value));
  if (!rows) return null;
  const totalRows = rows.filter((row) => CATEGORY_BUDGET_TYPES.has(row.category));
  const populatedRows = totalRows.filter((row) => row.budget != null && Number.isFinite(Number(row.budget)));
  const currencies = [...new Set(populatedRows.map((row) => String(row.currency || "").trim().toUpperCase()).filter(Boolean))];
  if (populatedRows.length === 0 || currencies.length > 1 || populatedRows.some((row) => !row.currency)) return null;
  return {
    amount: populatedRows.reduce((sum, row) => sum + Number(row.budget), 0),
    currency: currencies[0] || "USD",
    source: "category",
  };
}

export function getProjectBudget(project) {
  // Lean Projects surfaces intentionally use the single approved-budget field.
  // Keep getCategoryBudget above for compatibility with future restoration, but
  // do not let child category rows replace the approved figure here.
  return {
    amount: project?.approvedBudget == null ? null : Number(project.approvedBudget),
    currency: project?.currency || project?.budgetCurrency || "USD",
    source: "approved",
  };
}

export function convertProjectBudgetToUsd(project, rates) {
  const budget = getProjectBudget(project);
  return budget ? convertAmount(budget.amount, budget.currency, "USD", rates) : null;
}

export function convertProjectForecastToUsd(project, rates) {
  if (project?.forecastAtCompletion == null) return null;
  const budget = getProjectBudget(project);
  const currency = project.forecastCurrency || project.currency || budget?.currency || "USD";
  return convertAmount(project.forecastAtCompletion, currency, "USD", rates);
}

export function isCapitalOrMoveProject(project) {
  if (!project) return false;
  return ALWAYS_CAPITAL_OR_MOVE_TYPES.has(project.projectType);
}

export function applyProjectScope(projects, scope = PROJECT_SCOPE.CAPITAL_MOVES) {
  if (!Array.isArray(projects)) return [];
  return scope === PROJECT_SCOPE.ALL ? projects : projects.filter(isCapitalOrMoveProject);
}

export const PROJECT_DISPLAY_PHASES = Object.freeze(GANTT_LEGEND.map(({ key, label }) => [key, label]));

export function mapProjectPhaseToDisplayPhase(currentPhase) {
  if (typeof currentPhase === "string" && currentPhase.trim().toLowerCase() === "complete") return "complete";
  return mapGanttPhase(currentPhase)?.key || null;
}

export function getProjectPhaseDisplayLabel(currentPhase) {
  const displayPhase = mapProjectPhaseToDisplayPhase(currentPhase);
  if (displayPhase === "complete") return "Complete";
  return PROJECT_DISPLAY_PHASES.find(([key]) => key === displayPhase)?.[1]
    || PROJECT_PHASE[currentPhase]?.label
    || currentPhase
    || "—";
}

export function getProjectPhaseSteps(currentPhase) {
  const displayPhase = mapProjectPhaseToDisplayPhase(currentPhase);
  const currentIndex = PROJECT_DISPLAY_PHASES.findIndex(([key]) => key === displayPhase);
  const isComplete = displayPhase === "complete";
  return PROJECT_DISPLAY_PHASES.map(([key, label], index) => ({
    key,
    label,
    state: isComplete
      ? "done"
      : currentIndex < 0
        ? "upcoming"
        : index < currentIndex
          ? "done"
          : index === currentIndex
            ? "current"
            : "upcoming",
  }));
}

// ─── Default filter state ────────────────────────────────────────────────────

export const DEFAULT_PROJECT_FILTERS = {
  type:   "all",
  region: "all",
  phase:  "all",
  health: "all",
  owner:  "all",
};

// ─── Filter pipeline ─────────────────────────────────────────────────────────

/**
 * Apply the five filter dimensions to a project list.
 * Each dimension is independent; "all" means no restriction.
 *
 * Region is derived from the locationId prefix (AMER/EMEA/APAC).
 *
 * @param {Array}  projects  flat array of project objects (camelCase)
 * @param {object} filters   { type, region, phase, health, owner }
 * @returns {Array}
 */
export function applyProjectFilters(projects, filters) {
  if (!Array.isArray(projects)) return [];
  const f = { ...DEFAULT_PROJECT_FILTERS, ...filters };
  let result = projects;

  if (f.type !== "all") {
    result = result.filter((p) => p.projectType === f.type);
  }
  if (f.region !== "all") {
    result = result.filter((p) =>
      typeof p.locationId === "string" && p.locationId.startsWith(f.region + "-")
    );
  }
  if (f.phase !== "all") {
    result = result.filter((p) => p.phase === f.phase);
  }
  if (f.health !== "all") {
    result = result.filter((p) => p.statusOverall === f.health);
  }
  if (f.owner !== "all") {
    result = result.filter((p) => p.internalOwner === f.owner);
  }

  return result;
}

// ─── Stat derivation ─────────────────────────────────────────────────────────

/**
 * Compute the stat-bar values from the supplied project list.
 * Callers choose whether that list is the full portfolio or the current view.
 *
 * @param {Array} projects  full project list from getProjects()
 * @returns {object}
 *   { activeCount, totalBudget, forecastVariance, atRiskCount, criticalCount }
 */
function dateKey(value) {
  if (!value) return null;
  const raw = String(value);
  const date = new Date(raw.length === 10 ? `${raw}T00:00:00Z` : raw);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
}

function getCurrentCapacity(project) {
  if (project?.currentScheduleCapacity != null) return Number(project.currentScheduleCapacity);
  if (project?.currentCapacity != null) return Number(project.currentCapacity);
  return 0;
}

/**
 * Compute the visible landing-page KPIs. Rates and asOfDate are injectable
 * so mixed-currency and rolling-window behavior stays deterministic in tests.
 */
export function computeProjectStats(projects, { rates = { USD: 1 }, asOfDate = new Date() } = {}) {
  if (!Array.isArray(projects)) {
    return {
      activeCount: 0, intakeCount: 0, totalBudget: 0, forecastVariance: 0,
      atRiskCount: 0, criticalCount: 0, exitsInProgressCount: 0,
      netSeatsComing: 0, deliveringWithin12MonthsCount: 0,
      completedLast30d: 0, budgetUnavailableCount: 0, varianceUnavailableCount: 0, untrackedCount: 0,
    };
  }
  const active = projects.filter((p) => p.stage === "active");
  let totalBudget = 0;
  let forecastVariance = 0;
  let budgetUnavailableCount = 0;
  let varianceUnavailableCount = 0;

  for (const project of active) {
    const budgetUsd = project.financialsTracked === false ? null : convertProjectBudgetToUsd(project, rates);
    if (budgetUsd == null) {
      if (project.financialsTracked !== false && getProjectBudget(project).amount != null) budgetUnavailableCount += 1;
      continue;
    }
    totalBudget += budgetUsd;
    const forecastUsd = convertProjectForecastToUsd(project, rates);
    if (forecastUsd == null) {
      if (project.forecastAtCompletion != null) varianceUnavailableCount += 1;
      continue;
    }
    forecastVariance += forecastUsd - budgetUsd;
  }

  // Multiple projects can exist for one office; capacity is an office-level
  // fact and must be counted once in the visible set.
  const offices = new Set();
  let netSeatsComing = 0;
  for (const project of active) {
    if (!project.locationId || offices.has(project.locationId)) continue;
    offices.add(project.locationId);
    // plannedSeats is a read-only compatibility view of the future schedule.
    // A null value is an explicit schedule gap, not zero future seats.
    if (project.plannedSeats == null) continue;
    const planned = Number(project.plannedSeats);
    if (Number.isFinite(planned)) netSeatsComing += planned - getCurrentCapacity(project);
  }

  const asOf = dateKey(asOfDate);
  const endDate = asOf
    ? (() => {
        const date = new Date(`${asOf}T00:00:00Z`);
        date.setUTCFullYear(date.getUTCFullYear() + 1);
        return date.toISOString().slice(0, 10);
      })()
    : null;
  const startDate = asOf
    ? (() => {
        const date = new Date(`${asOf}T00:00:00Z`);
        date.setUTCDate(date.getUTCDate() - 30);
        return date.toISOString().slice(0, 10);
      })()
    : null;
  const completedLast30d = projects.filter((project) => {
    const actual = dateKey(project.completedOn);
    return project.stage === "completed" && actual && startDate && actual >= startDate && actual <= asOf;
  }).length;
  const deliveringWithin12MonthsCount = active.filter((project) => {
    const fdob = dateKey(project.fdobDate);
    return fdob && asOf && endDate && fdob >= asOf && fdob <= endDate;
  }).length;

  return {
    activeCount: active.length,
    intakeCount: projects.filter(p => p.stage === "intake").length,
    totalBudget,
    untrackedCount: active.filter(project => project.financialsTracked === false).length,
    forecastVariance,
    atRiskCount: active.filter((p) =>
      p.statusOverall === "at_risk"
      || p.statusOverall === "critical"
      || p.flagged === true
      || p.isFlagged === true
    ).length,
    criticalCount: active.filter((p) => p.statusOverall === "critical").length,
    exitsInProgressCount: active.filter((p) => p.projectType === "exit").length,
    netSeatsComing,
    deliveringWithin12MonthsCount,
    completedLast30d,
    budgetUnavailableCount,
    varianceUnavailableCount,
  };
}

/**
 * Count active projects of capex-relevant types.
 * Mirrors the logic in projectsClient.js countCapexActiveProjects, but
 * exported here so tests can import from the pure filter module.
 *
 * Capex types: new_build, renovation, critical_infra, compliance.
 *
 * @param {Array} projects
 * @returns {number}
 */
export function countCapexProjects(projects) {
  if (!Array.isArray(projects)) return 0;
  return projects.filter(
    (p) => CAPEX_PROJECT_TYPES.has(p.projectType) && p.stage === "active"
  ).length;
}

// ─── PR-2 selectors ──────────────────────────────────────────────────────────

/**
 * Count open decisions from a decisions array.
 * Used for the Decisions Needed stat in the portfolio stat bar.
 *
 * @param {Array} decisions  array of decision objects (camelCase)
 * @returns {number}
 */
export function countDecisionsNeeded(decisions) {
  if (!Array.isArray(decisions)) return 0;
  return decisions.filter((d) => d && d.status === "open").length;
}

/**
 * Sort risks by severity: high → med → low.
 * Does not mutate the input array.
 *
 * @param {Array} risks  array of risk objects (camelCase)
 * @returns {Array}  new sorted array
 */
export function sortRisksBySeverity(risks) {
  if (!Array.isArray(risks)) return [];
  const ORDER = { high: 0, med: 1, low: 2 };
  return [...risks].sort((a, b) => {
    const av = ORDER[a.severity] ?? 9;
    const bv = ORDER[b.severity] ?? 9;
    return av - bv;
  });
}

/**
 * Compute budget variance amount and percentage from a project object.
 * Returns { amount: number|null, pct: number|null }.
 * Both are null when the required inputs are missing.
 *
 * @param {object} project  camelCase project object
 * @returns {{ amount: number|null, pct: number|null }}
 */
export function computeBudgetVariance(project) {
  if (!project) return { amount: null, pct: null };
  const approved  = Number(project.approvedBudget);
  const forecast  = Number(project.forecastAtCompletion);
  if (!isFinite(approved) || !isFinite(forecast)) return { amount: null, pct: null };
  const amount = forecast - approved;
  const pct    = approved > 0 ? Math.round((amount / approved) * 1000) / 10 : null;
  return { amount, pct };
}

/**
 * Compute schedule variance in days from a project object.
 * Returns null when the required date fields are missing.
 *
 * @param {object} project  camelCase project object
 * @returns {number|null}  days late (positive = behind, negative = ahead)
 */
export function computeScheduleVariance(project) {
  if (!project) return null;
  if (!project.fdobDate || !project.forecastCompletionDate) return null;
  const target   = new Date(project.fdobDate);
  const forecast = new Date(project.forecastCompletionDate);
  if (isNaN(target.getTime()) || isNaN(forecast.getTime())) return null;
  return Math.round((forecast - target) / (1000 * 60 * 60 * 24));
}

// ─── Derived variance formatters ─────────────────────────────────────────────

/**
 * Format a budget variance percentage for display.
 * Returns "+8.0%" for over, "-5.0%" for under, "—" when null.
 */
export function fmtBudgetVarPct(pct) {
  if (pct == null) return "—";
  const n = Number(pct);
  if (!isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
}

/**
 * Format a schedule variance in days for display.
 * Returns "+15 days" for behind, "-7 days" for ahead, "—" when null.
 */
export function fmtScheduleVar(days) {
  if (days == null) return "—";
  const n = Number(days);
  if (!isFinite(n)) return "—";
  if (n === 0) return "On time";
  const sign = n > 0 ? "+" : "";
  const abs = Math.abs(n);
  if (abs >= 7) {
    const weeks = Math.round(abs / 7);
    return n > 0 ? `+${weeks}w` : `-${weeks}w`;
  }
  return `${sign}${n}d`;
}

/**
 * Format an approved_budget or forecast total for display.
 * Returns "$45.0M USD" for large values, "$2.2M USD" for smaller, "—" for null.
 */
export function fmtBudgetAmount(amount, currency, { withSymbol = true, zeroAsMissing = true } = {}) {
  if (amount == null || (zeroAsMissing && amount === 0)) return "—";
  const n = Number(amount);
  if (!isFinite(n)) return "—";
  const ccy = currency || "USD";
  const symbol = withSymbol ? getCurrencySymbol(ccy) : ccy;
  const prefix = symbol !== ccy ? symbol : "";
  if (n >= 1_000_000) return `${prefix}${(n / 1_000_000).toFixed(1)}M ${ccy}`;
  if (n >= 1_000)     return `${prefix}${(n / 1_000).toFixed(0)}K ${ccy}`;
  return `${prefix}${n.toFixed(0)} ${ccy}`;
}

// ─── Health key mapping ───────────────────────────────────────────────────────

/**
 * Map a project status ('on_track'/'at_risk'/'critical') to the HEALTH
 * token key ('green'/'yellow'/'red') used by HealthDot.
 * Returns null for unknown values → HealthDot renders default gray.
 */
export function projectStatusToHealthKey(status) {
  if (status === "on_track") return "green";
  if (status === "at_risk")  return "yellow";
  if (status === "critical") return "red";
  return null;
}
